import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import ErrorBoundary from '@rover/components/src/errorHandling/ErrorBoundary';
import ShortlistPreview from '@rover/components/src/shortlist/components/ShortlistPreview';
import { ShortlistPreviewTargetProvider } from '@rover/components/src/shortlist/ShortlistPreviewTargetContext';
import { LAYOUT_TOP_FILTERS } from '@rover/constants/src/search/searchPage.constants';
import { Box, Flex } from '@rover/kibble/core';
import MQ, { SM_MIN } from '@rover/kibble/styles/MediaQuery';
import { getPerfHackIsClosed } from '@rover/react-lib/src/components/modals/Modal/Modal.common';
import type { BreakpointMatch } from '@rover/react-lib/src/hooks/useMatchMedia';
import { withMatchMedia } from '@rover/react-lib/src/hooks/useMatchMedia';
import WizardFiltersCollectionContainerV2 from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Collections/WizardFiltersCollectionContainerV2';
import PreventScrollOnLoad from '@rover/react-lib/src/pages/search/SearchPage/components/PreventScrollOnLoad';
import type { SearchResultsObservation } from '@rover/react-lib/src/pages/search/SearchPage/SearchPage.types';
import { useSimplifiedFilters } from '@rover/react-lib/src/pages/search/SearchPage/utilities/useSimplifiedFilters';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import ColorfulPerfectMatchSectionContainer from './components/ColorfulPerfectMatchSection';
import BooleanFilters from './components/Filters/BooleanFilters';
import { SearchByNameButton } from './components/SearchByName';
import { useSearchByNameRedirect } from './components/SearchByName/hooks/useSearchByNameRedirect';
import { SearchPageLayoutTopFilters } from './components/SearchPageLayoutTopFilters';
import SimplifiedFiltersBarContainer from './components/SimplifiedFiltersBar';
import {
  AdvancedFiltersButtonFixed,
  AdvancedFiltersModal,
  CondensedFiltersCollection,
  SearchPaginationContainer,
  SearchResults,
  SearchWizardModal,
  StickyFiltersBar,
  WizardFiltersCollection,
} from './components';
import HtmlHeader from './HtmlHeaderContainer';

export type Props = {
  isModalDisabled: boolean;
  advancedFiltersCount: number;
  fireSearchFiltersClickEvent: () => void;
  isDateConstrained: boolean;

  updateFiltersAndFireSearchOnChange: (filters: Partial<SearchFilters>) => void;
  matchesMediaQuery: BreakpointMatch;
  inVariantSimplifiedMobileOwnerSearchFormExperiment: boolean;
  hasPets: boolean;
  showDynamicMap: boolean;

  setShowDynamicMap: (arg0: boolean) => void;
};

const StyledAdvancedFiltersModal = styled(AdvancedFiltersModal)`
  ${MQ.SM_UP.toString()} {
    display: none;
  }
`;

const SearchPage = ({
  isModalDisabled,
  advancedFiltersCount,
  fireSearchFiltersClickEvent,
  isDateConstrained,
  updateFiltersAndFireSearchOnChange,
  matchesMediaQuery = false,
  inVariantSimplifiedMobileOwnerSearchFormExperiment,
  hasPets,
  showDynamicMap,
  setShowDynamicMap,
}: Props): JSX.Element => {
  const { isActive: isUseSimplifiedFiltersActive } = useSimplifiedFilters();
  const isSearchByNameRequested = useSearchByNameRedirect();
  const [scrollToResults, setScrollToResults] = useState<boolean>(false);
  const [showAdvancedFiltersModal, setShowAdvancedFiltersModal] = useState<boolean>(false);
  const [insideSearchResults, setInsideSearchResults] = useState<boolean>(false);

  const [showWizardFilters, setShowWizardFilters] = useState<boolean>(
    isUseSimplifiedFiltersActive ? true : !isModalDisabled
  );
  const [showPaginatedWizardModal, setShowPaginatedWizardModal] = useState<boolean>(true);
  const searchCardRefs = useRef<HTMLDivElement[]>([]);
  const resultsRef = useRef<HTMLElement | null>(null);

  const determineIfWizardModalVisible = (): boolean => {
    if (isUseSimplifiedFiltersActive) return false;
    return getPerfHackIsClosed() ? false : !isModalDisabled && (!isDateConstrained || !hasPets);
  };

  // creating this "initially" value because it is typically a react anti-pattern to use props for initializing state
  const initiallyShowWizardFilters = !isModalDisabled;

  const actuallyShowPaginatedWizardModal =
    determineIfWizardModalVisible() && showPaginatedWizardModal && !isSearchByNameRequested;

  if (!actuallyShowPaginatedWizardModal && !showDynamicMap) {
    setShowDynamicMap(true);
  }

  const handleObserveSearchResults = ({
    viewportIsBelowTop,
    viewportIsAboveBottom,
  }: SearchResultsObservation): void => {
    setInsideSearchResults(viewportIsBelowTop && viewportIsAboveBottom);
  };

  const openAdvancedFiltersModal = (): void => {
    setScrollToResults(false);
    setShowAdvancedFiltersModal(true);
    fireSearchFiltersClickEvent();
  };

  const closeAdvancedFiltersModal = (shouldScrollToResults: boolean): void => {
    setScrollToResults(shouldScrollToResults);
    setShowAdvancedFiltersModal(false);
    setShowWizardFilters(false);
  };

  const handleSaveSearchInformationButtonClick = (): void => {
    setShowWizardFilters(false);
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    window.scrollTo(0, 0);
  };

  const handlePageChange = (page: number): void => {
    updateFiltersAndFireSearchOnChange({ page });
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMarkerClick = (index: number): void => {
    const searchCardRef = searchCardRefs.current[index];
    searchCardRef &&
      searchCardRef.scrollIntoView({
        behavior: 'smooth',
      });
  };

  const scrollToTop = (): void => {
    // scroll to top after closing filters modal
    setTimeout(() => {
      // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleSearchCardRefs = (ref: HTMLDivElement, index: number): void => {
    searchCardRefs.current[index] = ref;
  };

  const renderFiltersCollection = (): JSX.Element | null => {
    if (isUseSimplifiedFiltersActive) return null;
    if (!initiallyShowWizardFilters || !showWizardFilters) {
      return <CondensedFiltersCollection showAdvancedFiltersModal={openAdvancedFiltersModal} />;
    }
    if (inVariantSimplifiedMobileOwnerSearchFormExperiment) {
      return (
        <WizardFiltersCollectionContainerV2 onSubmit={handleSaveSearchInformationButtonClick} />
      );
    }
    return <WizardFiltersCollection onSubmit={handleSaveSearchInformationButtonClick} />;
  };

  return (
    <ShortlistPreviewTargetProvider>
      <HtmlHeader />

      {matchesMediaQuery && (
        <ErrorBoundary location="SearchPagePaginatedWizardModal">
          <PreventScrollOnLoad preventScrollOnLoad={actuallyShowPaginatedWizardModal} />
          <SearchWizardModal
            isOpen={actuallyShowPaginatedWizardModal}
            onClose={() => setShowPaginatedWizardModal(false)}
          />
        </ErrorBoundary>
      )}

      <ErrorBoundary location="SearchPageFilters">
        {isUseSimplifiedFiltersActive && !showWizardFilters && (
          <StickyFiltersBar onSave={scrollToTop} />
        )}
        {isUseSimplifiedFiltersActive && showWizardFilters && (
          <ColorfulPerfectMatchSectionContainer onSubmit={handleSaveSearchInformationButtonClick} />
        )}
        <Box display={['block', 'none']}>{renderFiltersCollection()}</Box>
      </ErrorBoundary>

      <ErrorBoundary location="SimplifiedFiltersBar">
        <Box
          display={['none', 'block']}
          borderTop="1px solid"
          borderColor="border.primary"
          background="primary"
        >
          <Flex
            justifyContent="center"
            alignItems="center"
            my="5x"
            px={LAYOUT_TOP_FILTERS.GUTTER}
            gap="4x"
            maxWidth={LAYOUT_TOP_FILTERS.AVAILABLE_WIDTH}
            sx={{ marginX: 'auto' }}
          >
            <SimplifiedFiltersBarContainer />
            <BooleanFilters count={advancedFiltersCount} />
            <SearchByNameButton />
          </Flex>
        </Box>
      </ErrorBoundary>
      <SearchPageLayoutTopFilters onMarkerClick={handleMarkerClick}>
        <SearchResults
          onObserve={handleObserveSearchResults}
          scrollToResults={scrollToResults}
          searchCardRefs={handleSearchCardRefs}
          resultsRef={resultsRef}
          isSeoPage={false}
          showWizardFilters={showWizardFilters}
        />
      </SearchPageLayoutTopFilters>

      <ErrorBoundary location="SearchPagePagination">
        <SearchPaginationContainer onPageChange={handlePageChange} />
      </ErrorBoundary>

      <ErrorBoundary location="SearchPageAdvancedFilters">
        {!isUseSimplifiedFiltersActive && insideSearchResults && (
          <AdvancedFiltersButtonFixed
            selectedFilterCount={advancedFiltersCount}
            onClick={openAdvancedFiltersModal}
          />
        )}
        <StyledAdvancedFiltersModal
          isOpen={showAdvancedFiltersModal && !matchesMediaQuery}
          onClose={closeAdvancedFiltersModal}
        />
      </ErrorBoundary>

      <ErrorBoundary location="SearchPageShortlistPreview">
        <ShortlistPreview />
      </ErrorBoundary>
    </ShortlistPreviewTargetProvider>
  );
};

export default withMatchMedia(`(min-width:${SM_MIN}px)`)(SearchPage);
